import { ClockCircleOutlined } from "@ant-design/icons";
import { TableProps, Flex, Typography, InputRef } from "antd";
import { bookingStatusTone } from "components/bookings/bookingStatusTone";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { th, tnum } from "shared/components/TableText";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { BookingDto } from "shared/types";
import { BookingStatus } from "shared/types/enum";
import { formatDateToLocalString, getBookingStatusLabel } from "shared/utils";
import { formatDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const CustomerBookingTableColumn = ({
  showSearchStaff,
  searchInput,
  sortAscending = true,
  showAttendee = false,
  membership = false,
}: {
  sortAscending?: boolean;
  showSearchStaff: boolean;
  searchInput?: React.RefObject<InputRef>;
  /** Name the person who attended. Only meaningful where one list can contain
   *  several people's bookings — a SHARED package's booking list does, since it
   *  is scoped to the package rather than to one client. */
  showAttendee?: boolean;
  /** The list is a membership's own bookings: they take no credits, so the
   *  Kredit column reads a dash. The client-wide list has no per-row flag. */
  membership?: boolean;
}): TableProps<BookingDto>["columns"] => [
  {
    title: th(translate("label.booking_date")),
    dataIndex: "booking_date",
    key: "booking_date",
    render: (_, { booking_date }) => (
      <span style={tnum}>{formatDate(booking_date)}</span>
    ),
  },
  ...(showAttendee
    ? [
        {
          title: th(translate("customer")),
          dataIndex: "customer_name",
          key: "customer_name",
          render: (_: unknown, { customer_name }: BookingDto) => (
            <span style={{ fontWeight: 600 }}>{customer_name ?? "-"}</span>
          ),
        },
      ]
    : []),
  {
    title: th(translate("label.booking_code")),
    dataIndex: "booking_ref",
    key: "booking_ref",
    // Null for bookings that predate the booking_ref column.
    render: (_, { booking_ref }) => (
      <span style={{ ...tnum, color: colors.ink2 }}>{booking_ref ?? "-"}</span>
    ),
  },
  {
    title: th(translate("class")),
    dataIndex: "class_name",
    key: "class_name",
    sorter: (a: BookingDto, b: BookingDto) => {
      const dateA = a.schedule_date ? new Date(a.schedule_date).getTime() : 0;
      const dateB = b.schedule_date ? new Date(b.schedule_date).getTime() : 0;
      return dateA - dateB;
    },
    sortOrder: sortAscending ? "ascend" : "descend",
    render: (_: string, { class_name, schedule_date }: BookingDto) => {
      return (
        <Flex vertical gap={10}>
          <Typography.Text strong>{class_name}</Typography.Text>
          {schedule_date && (
            <Flex gap={4} align="center">
              <ClockCircleOutlined style={{ fontSize: textPx(11) }} />
              <Typography.Text style={{ fontSize: textPx(12) }}>{formatDateToLocalString(schedule_date)}</Typography.Text>
            </Flex>
          )}
        </Flex>
      );
    },
  },
  {
    // Credits the booking took (its own snapshot).
    title: th(translate("label.credits")),
    dataIndex: "credit_cost",
    key: "credit_cost",
    render: (_, { credit_cost, package_unlimited }) => (
      <span style={tnum}>{membership || package_unlimited ? "-" : (credit_cost ?? 1)}</span>
    ),
  },
  {
    title: th(translate("instructor")),
    dataIndex: "staff_name",
    key: "staff_name",
    ...(showSearchStaff && searchInput ? { ...getColumnSearchProps({ config: "staff_name", searchInput }) } : {}),
  },
  {
    title: th(translate("label.booking_status")),
    dataIndex: "booking_status",
    key: "booking_status",
    render: (_, { booking_status }) => (
      <StatusBadge
        tone={bookingStatusTone(booking_status)}
        label={getBookingStatusLabel(booking_status)}
      />
    ),
    // ACTIVE BOOKINGS is sorted ascendingly and don't need any filters by booking status
    ...(!sortAscending
      ? {
          ...{
            // Exact match on the raw enum value (`includes` also matched
            // Waitlist Expired when Waitlisted was ticked).
            onFilter: (value, record) => record.booking_status === value,
            filters: Object.values(BookingStatus)
              .filter((bs) => bs !== BookingStatus.Confirmed)
              .map((value) => ({
                value,
                text: getBookingStatusLabel(value),
              })),
          },
        }
      : {}),
  },
];

export default CustomerBookingTableColumn;
